// Minimal RRULE support (REQ-CAL-011) for the common household cases: DAILY, WEEKLY (by weekday),
// MONTHLY (by month-day), with an optional INTERVAL. Pure. Occurrences preserve the series start's
// UTC time-of-day (a household reminder may drift ±1h across a DST boundary — acceptable for v1).
const DAY = 86_400_000;
const WD = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"]; // RFC5545 weekday codes, Sun=0

export interface RRule {
  freq: "DAILY" | "WEEKLY" | "MONTHLY";
  interval: number;
  byday: number[]; // 0=Sun..6=Sat
  bymonthday: number[]; // 1..31
}

/** Parse an RRULE string (e.g. "FREQ=WEEKLY;BYDAY=TH") into a typed rule, or null if unusable. */
export function parseRRule(s: string | null | undefined): RRule | null {
  if (!s) return null;
  const parts: Record<string, string> = {};
  for (const kv of s.replace(/^RRULE:/i, "").split(";")) {
    const [k, v] = kv.split("=");
    if (k && v) parts[k.trim().toUpperCase()] = v.trim().toUpperCase();
  }
  const freq = parts.FREQ;
  if (freq !== "DAILY" && freq !== "WEEKLY" && freq !== "MONTHLY") return null;
  const interval = Math.max(1, parseInt(parts.INTERVAL ?? "1", 10) || 1);
  const byday = (parts.BYDAY ?? "").split(",").map((d) => WD.indexOf(d.trim())).filter((i) => i >= 0);
  const bymonthday = (parts.BYMONTHDAY ?? "").split(",").map((d) => parseInt(d, 10)).filter((n) => n >= 1 && n <= 31);
  return { freq, interval, byday, bymonthday };
}

/** Complete one occurrence of a recurring TASK (REQ-ITEM-008). If the item has a usable rrule and a
 *  next occurrence exists (after both its current due AND when it was completed, so an early or late
 *  tick still advances by one), it stays OPEN with due_at advanced; otherwise (no rrule, or a bounded
 *  rule that has ended) it's DONE. Pure — the lifecycle machine (`nextStatus`) stays separate. */
export function completeRecurringTask(item: { rrule?: string | null; due_at?: string | null }, completedAtISO: string): { status: "open" | "done"; due_at?: string } {
  const anchor = item.due_at ?? null;
  if (item.rrule && anchor) {
    const a = Date.parse(anchor), c = Date.parse(completedAtISO);
    const after = new Date(Math.max(Number.isFinite(a) ? a : c, Number.isFinite(c) ? c : a)).toISOString();
    const next = nextOccurrence(item.rrule, anchor, after);
    if (next) return { status: "open", due_at: next };
  }
  return { status: "done" };
}

/** The small repeat picker (REQ-ITEM-008): a preset ⇄ rrule map for the common household cadences.
 *  The series anchors on the item's due date, so a plain FREQ (no BYDAY/BYMONTHDAY) already lands on
 *  the right weekday / month-day. "none" = a one-off (rrule cleared). */
export const REPEAT_PRESETS: ReadonlyArray<{ value: string; label: string; rrule: string }> = [
  { value: "none", label: "Doesn't repeat", rrule: "" },
  { value: "daily", label: "Every day", rrule: "FREQ=DAILY" },
  { value: "weekly", label: "Every week", rrule: "FREQ=WEEKLY" },
  { value: "fortnightly", label: "Every 2 weeks", rrule: "FREQ=WEEKLY;INTERVAL=2" },
  { value: "monthly", label: "Every month", rrule: "FREQ=MONTHLY" },
];

/** The preset rrule for a picker value (empty string for none/unknown). */
export function rruleForPreset(value: string): string {
  return REPEAT_PRESETS.find((p) => p.value === value)?.rrule ?? "";
}

/** The picker value that matches a stored rrule — so editing an existing repeat shows the right option.
 *  A custom/complex rule (e.g. specific BYDAY, or an interval we don't offer) maps to "custom". */
export function presetForRrule(s: string | null | undefined): string {
  if (!s || !s.trim()) return "none";
  const r = parseRRule(s);
  if (!r) return "none";
  if (r.freq === "DAILY" && r.interval === 1 && !r.byday.length) return "daily";
  if (r.freq === "WEEKLY" && r.interval === 1 && !r.byday.length) return "weekly";
  if (r.freq === "WEEKLY" && r.interval === 2 && !r.byday.length) return "fortnightly";
  if (r.freq === "MONTHLY" && r.interval === 1 && !r.bymonthday.length) return "monthly";
  return "custom"; // a richer rule (from AI refine / capture) — keep it, show it as-is
}

/** A short human label for a rule ("every Thu", "daily", "monthly"). */
export function recurrenceLabel(s: string | null | undefined): string {
  const r = parseRRule(s);
  if (!r) return "";
  if (r.freq === "DAILY") return r.interval > 1 ? `every ${r.interval} days` : "daily";
  if (r.freq === "MONTHLY") return r.interval > 1 ? `every ${r.interval} months` : "monthly";
  const days = (r.byday.length ? r.byday : []).map((d) => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d]).join("/");
  const every = r.interval > 1 ? `every ${r.interval} wks` : "every";
  return days ? `${every} ${days}` : "weekly";
}

/** Occurrence ISO strings of `rrule` (series starting at `startISO`) within [fromISO, toISO]. */
export function occurrencesBetween(rruleStr: string | null | undefined, startISO: string | null | undefined, fromISO: string, toISO: string, max = 200): string[] {
  const r = parseRRule(rruleStr);
  if (!r || !startISO) return [];
  const start = Date.parse(startISO);
  const from = Date.parse(fromISO);
  const to = Date.parse(toISO);
  if (Number.isNaN(start) || Number.isNaN(from) || Number.isNaN(to) || to < start) return [];
  const sd = new Date(start);
  const hh = sd.getUTCHours(), mm = sd.getUTCMinutes(), ss = sd.getUTCSeconds();
  const out: string[] = [];

  if (r.freq === "DAILY") {
    let t = start;
    if (t < from) t += Math.ceil((from - t) / (DAY * r.interval)) * DAY * r.interval;
    for (; t <= to && out.length < max; t += DAY * r.interval) if (t >= from && t >= start) out.push(new Date(t).toISOString());
    return out;
  }

  if (r.freq === "WEEKLY") {
    const days = r.byday.length ? r.byday : [sd.getUTCDay()];
    const startMidUTC = Date.UTC(sd.getUTCFullYear(), sd.getUTCMonth(), sd.getUTCDate());
    for (let cursor = startMidUTC; cursor <= to && out.length < max; cursor += DAY) {
      const dt = new Date(cursor);
      const weeks = Math.floor((cursor - startMidUTC) / (DAY * 7));
      if (days.includes(dt.getUTCDay()) && weeks % r.interval === 0) {
        const occ = Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate(), hh, mm, ss);
        if (occ >= from && occ >= start && occ <= to) out.push(new Date(occ).toISOString());
      }
    }
    return out;
  }

  // MONTHLY
  const mday = r.bymonthday.length ? r.bymonthday[0] : sd.getUTCDate();
  let y = sd.getUTCFullYear(), mo = sd.getUTCMonth();
  for (let i = 0; i < max; i++) {
    const occ = Date.UTC(y, mo, mday, hh, mm, ss);
    if (occ > to) break;
    if (occ >= from && occ >= start) out.push(new Date(occ).toISOString());
    mo += r.interval;
    while (mo > 11) { mo -= 12; y += 1; }
  }
  return out;
}

/** The next occurrence strictly after `afterISO` (looks ahead ~15 months), or null. */
export function nextOccurrence(rruleStr: string | null | undefined, startISO: string | null | undefined, afterISO: string): string | null {
  const from = new Date(Date.parse(afterISO) + 1000).toISOString();
  const to = new Date(Date.parse(afterISO) + 460 * DAY).toISOString();
  return occurrencesBetween(rruleStr, startISO, from, to, 1)[0] ?? null;
}
